import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { IoOpenOutline } from 'react-icons/io5';

import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalHeader,
  SectionContainer,
  StyledModalBody,
  StyledModalV2
} from '../../components/Modals';
import { ButtonBodyVariant } from '../../components/ui/ButtonVariant';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import { Flex } from '../../components/ui/Flex';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import { imageString } from '../../helper';
import { DATE_FORMATS } from '../users/constant';
import {
  canManageDocument,
  DOCUMENT_FILE_TYPE_CATEGORY,
  DOCUMENT_FILE_TYPE_CATEGORY_STYLES,
  DOC_VISIBILITY_MAP,
  getDocumentFileTypeCategory,
  getDocumentFileTypeChipLabel,
  isDocumentInlinePreviewable
} from './constants';
import {
  DocumentDetailsThumbnail,
  DocumentDetailsThumbnailImage,
  DocumentFileMetaLabel,
  DocumentFileMetaRow,
  DocumentFileMetaValue,
  DocumentFileName,
  DocumentPreviewFrame,
  DocumentPreviewImage,
  DocumentPreviewPdf,
  DocumentPreviewVideo,
  DocumentTypeBadge,
  DocumentTypePlaceholder,
  DocumentTypePlaceholderContent,
  DocumentTypePlaceholderLabel,
  DocumentTypePlaceholderOpen
} from './documentWizardStyles';

const DocumentDetailsPreview = ({ fileName, url, onOpen, openLabel }) => {
  const fileTypeLabel = getDocumentFileTypeChipLabel(fileName);
  const category = getDocumentFileTypeCategory(fileName);
  const styles = category ? DOCUMENT_FILE_TYPE_CATEGORY_STYLES[category] : null;
  const canInlinePreview = Boolean(url) && isDocumentInlinePreviewable(fileName);

  if (canInlinePreview && category === DOCUMENT_FILE_TYPE_CATEGORY.IMG) {
    return (
      <DocumentPreviewFrame>
        <DocumentPreviewImage src={url} alt={fileName || 'Document preview'} />
      </DocumentPreviewFrame>
    );
  }

  if (canInlinePreview && category === DOCUMENT_FILE_TYPE_CATEGORY.PDF) {
    return (
      <DocumentPreviewFrame>
        <DocumentPreviewPdf src={url} title={fileName || 'PDF preview'} />
      </DocumentPreviewFrame>
    );
  }

  if (canInlinePreview && category === DOCUMENT_FILE_TYPE_CATEGORY.VID) {
    return (
      <DocumentPreviewFrame>
        <DocumentPreviewVideo src={url} controls preload="metadata" />
      </DocumentPreviewFrame>
    );
  }

  if (!styles) {
    return null;
  }

  return (
    <DocumentTypePlaceholder $background={styles.background} $interactive={Boolean(url)}>
      <DocumentTypePlaceholderContent>
        <DocumentTypePlaceholderLabel $color={styles.color}>
          {fileTypeLabel}
        </DocumentTypePlaceholderLabel>
        {url ? (
          <DocumentTypePlaceholderOpen
            type="button"
            className="document-type-placeholder-open"
            onClick={onOpen}
            aria-label={openLabel}>
            <IoOpenOutline size={18} />
            {openLabel}
          </DocumentTypePlaceholderOpen>
        ) : null}
      </DocumentTypePlaceholderContent>
    </DocumentTypePlaceholder>
  );
};

const ViewDocumentModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalBodyRef = useRef(null);
  const { isAtBottom } = useScrollToBottom(modalBodyRef);
  const modal = useSelector((state) => state.headerModal.modal);
  const document = useSelector((state) => state.headerModal.data);
  const roleId = useSelector((state) => state.auth.roleId);
  const allBuildings = useSelector((state) => state.building.allBuildings);
  const globalBuildingId = useSelector((state) => state.building.globalBuildingId);

  const accessibleBuildingIds = (allBuildings || []).map((building) => building.id);
  if (
    globalBuildingId > 0 &&
    !accessibleBuildingIds.some((id) => Number(id) === Number(globalBuildingId))
  ) {
    accessibleBuildingIds.push(globalBuildingId);
  }
  const canEdit = canManageDocument(document, roleId, accessibleBuildingIds);

  const closeModal = () => {
    dispatch(changeOpenedModal({ modal: '' }));
  };

  const handleEdit = () => {
    if (!canEdit) {
      return;
    }
    dispatch(
      changeOpenedModal({
        modal: 'addEditDocument',
        data: document
      })
    );
  };

  const openDocument = () => {
    if (document?.url) {
      window.open(document.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!document) {
    return null;
  }

  const fileTypeLabel = getDocumentFileTypeChipLabel(document.file_name);
  const fileCategory = getDocumentFileTypeCategory(document.file_name);
  const fileTypeStyles = fileCategory
    ? DOCUMENT_FILE_TYPE_CATEGORY_STYLES[fileCategory]
    : null;
  const thumbnailRaw = document.thumbnail || document.thumbnail_url || '';
  const thumbnailUrl = (() => {
    const trimmed = String(thumbnailRaw).trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.startsWith('http') ? trimmed : imageString(trimmed);
  })();

  return (
    <StyledModalV2 show={modal === 'viewDocumentModal'} side width="550px">
      <StyledModalBody innerRef={(el) => (modalBodyRef.current = el)}>
        <ModalHeader title={t('Document Details')} v2 />
        <ModalBodyContent>
          <SectionContainer>
            <Flex direction="column" gap="16px">
              <DocumentDetailsPreview
                fileName={document.file_name}
                url={document.url}
                onOpen={openDocument}
                openLabel={t('Open file')}
              />
              <Flex direction="column" gap="12px">
                <DocumentFileName>{document.name || '-'}</DocumentFileName>
                <Flex alignItems="center" gap="16px">
                  {thumbnailUrl ? (
                    <DocumentDetailsThumbnail>
                      <DocumentDetailsThumbnailImage
                        src={thumbnailUrl}
                        alt={t('Document thumbnail', { defaultValue: 'Document thumbnail' })}
                      />
                    </DocumentDetailsThumbnail>
                  ) : null}
                  <Flex direction="column" gap="8px" style={{ flex: 1, minWidth: 0 }}>
                    <DocumentFileMetaRow>
                      <DocumentFileMetaLabel>{t('Visibility')}</DocumentFileMetaLabel>
                      <DocumentFileMetaValue>
                        {t(DOC_VISIBILITY_MAP[document.visibility] || '-')}
                      </DocumentFileMetaValue>
                    </DocumentFileMetaRow>
                    <DocumentFileMetaRow>
                      <DocumentFileMetaLabel>{t('Date')}</DocumentFileMetaLabel>
                      <DocumentFileMetaValue>
                        {moment(document.create_date).format(DATE_FORMATS.MMM_DD_YYYY)}
                      </DocumentFileMetaValue>
                    </DocumentFileMetaRow>
                    <DocumentFileMetaRow>
                      <DocumentFileMetaLabel>{t('File type')}</DocumentFileMetaLabel>
                      <DocumentFileMetaValue>
                        {fileTypeStyles ? (
                          <DocumentTypeBadge
                            $background={fileTypeStyles.background}
                            $color={fileTypeStyles.color}>
                            {fileTypeLabel}
                          </DocumentTypeBadge>
                        ) : (
                          fileTypeLabel
                        )}
                      </DocumentFileMetaValue>
                    </DocumentFileMetaRow>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </SectionContainer>
        </ModalBodyContent>
      </StyledModalBody>

      <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={isAtBottom}>
        {document.url ? (
          <ButtonBodyVariant variant="secondary" type="button" onClick={openDocument}>
            <IoOpenOutline size={18} className="mr-1" />
            {t('Open file')}
          </ButtonBodyVariant>
        ) : null}
        <ButtonBodyVariant
          variant={canEdit ? 'secondary' : undefined}
          type="button"
          onClick={closeModal}>
          {t('Close')}
        </ButtonBodyVariant>
        {canEdit ? (
          <ButtonBodyVariant type="button" onClick={handleEdit}>
            {t('Edit')}
          </ButtonBodyVariant>
        ) : null}
      </ModalButtonsContainer>
    </StyledModalV2>
  );
};

export default ViewDocumentModal;
